import {
  ReactNode,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useNavigate } from 'react-router-dom';
import { useIntercom } from 'react-use-intercom';

import accountsAPI from '@/api/accounts';
import analyticsAPI from '@/api/analytics';
import {
  broadcastLogout,
  clearAuthState,
  onLogoutBroadcast,
} from '@/helpers/authCleanup';
import { IPlatformSubscriptionFull } from '@/types/IOrganisation';
import IUser from '@/types/IUser';
import SubscriptionStatus from '@/types/SubscriptionStatus';
import { detectUserCountry } from '@/utils/countryDetection';
import { getRedirectUrl } from '@/utils/shopifyRedirect';

interface ILogin {
  email: string;
  password: string;
  rememberMe?: boolean;
  activationKey?: string;
  activationKeyFromLogin?: string;
}
interface AuthContextType {
  user?: IUser;
  userScope?: string;
  userRole?: string;
  orgID?: number;
  isUserViewForAdmin?: boolean;
  loading: boolean;
  authError?: boolean;
  platformSubscriptionStatus: string;
  platformSubscription?: IPlatformSubscriptionFull | null;
  totalProfiles: number;
  features?: string[];
  adminRole?: string | null;
  getCurrentUser: () => void;
  refreshCurrentUser: () => Promise<void>;
  setScopeAndOrg: (
    scope?: string,
    ID?: number,
    userViewForAdmin?: boolean,
    userRole?: string,
    features?: string[],
    adminRole?: string | null,
    platformSubscriptionStatus?: string,
  ) => void;
  login: ({
    email,
    password,
    rememberMe,
    activationKeyFromLogin,
  }: ILogin) => void;
  signUp: (
    email: string,
    firstName: string,
    lastName: string,
    organisationName: string,
    password: string,
    passwordConfirmation: string,
    termsAccepted: boolean,
    activationKey?: string,
  ) => void;
  verifyMfaCode: (mfa_token: string, code: string) => void;
  linkSocialAccount: (password: string, verify_token: string) => Promise<void>;
  logout: () => void;
  hasFeature: (feature: string) => boolean;
  setAuthError: (error: boolean) => void;
  shopifyLogin: ({ email, password, rememberMe }: ILogin) => void;
  redirectToShopify: () => void;
  ssoLogin: (token: string) => void;
}

const AuthContext = createContext<AuthContextType>({} as AuthContextType);

export function AuthProvider({ children }: { children: ReactNode }) {
  const navigate = useNavigate();
  const {
    update: intercomUpdate,
    shutdown: intercomShutdown,
    boot: intercomBoot,
  } = useIntercom();

  const [user, setUser] = useState<IUser>();
  const [userScope, setUserScope] = useState<string>();
  const [userRole, setUserRole] = useState<string>();
  const [platformSubscriptionStatus, setPlatformSubscriptionStatus] =
    useState<string>(SubscriptionStatus.FREE);
  const [platformSubscription, setPlatformSubscription] =
    useState<IPlatformSubscriptionFull | null>();
  const [totalProfiles, setTotalProfiles] = useState<number>(0);

  const [features, setFeatures] = useState<string[]>([]);
  const [adminRole, setAdminRole] = useState<string | null>();
  const [orgID, setOrgID] = useState<number>();
  const [isUserViewForAdmin, setIsUserViewForAdmin] = useState<boolean>();
  const [authError, setAuthError] = useState<boolean>();
  const [loading, setLoading] = useState(false);
  const [loadingInitial, setLoadingInitial] = useState(true);

  const setScopeAndOrg = (
    scope?: string,
    ID?: number,
    userViewForAdmin?: boolean,
    userRole?: string,
    features?: string[],
    adminRole?: string | null,
    platformSubscriptionStatus?: string,
  ) => {
    setUserScope(scope);
    setAdminRole(adminRole);
    setOrgID(ID);
    setIsUserViewForAdmin(userViewForAdmin);
    setUserRole(userRole);
    setFeatures(features ?? []);

    const status = platformSubscriptionStatus ?? SubscriptionStatus.FREE;
    setPlatformSubscriptionStatus(status);

    if (status === SubscriptionStatus.ACTIVE) {
      getPlatformSubscription(ID);
    }
  };

  const refreshCurrentUser = () => {
    setLoading(true);

    return accountsAPI
      .getCurrentUser()
      .then(res => res.data.data)
      .then(data => {
        setUser(data);
        setScopeAndOrg(
          data.scope,
          data.organisation_id,
          false,
          data.organisation_role,
          data.features,
          data.role,
          data.platform_subscription_status,
        );
      })
      .finally(() => setLoading(false));
  };

  const getCurrentUser = () => {
    setLoading(true);

    return accountsAPI
      .getCurrentUser()
      .then(res => {
        trackLoggedInUserWithIntercom(res.data.data);
        return res.data.data;
      })
      .then(data => {
        setUser(data);
        if (data.organisation_id !== null)
          analyticsAPI.recordLogin(data.organisation_id);
        setScopeAndOrg(
          data.scope,
          data.organisation_id,
          false,
          data.organisation_role,
          data.features,
          data.role,
          data.platform_subscription_status,
        );
      })
      .catch(() => {})
      .finally(() => {
        setLoading(false);
      });
  };

  useEffect(() => {
    if (loadingInitial) {
      getCurrentUser().finally(() => setLoadingInitial(false));
    }
  }, [loadingInitial]);

  /**
   * Resets authentication UI state and Intercom session.
   * Used by logout and multi-tab logout synchronization.
   */
  const resetAuthUI = useCallback(() => {
    setUser(undefined);
    setOrgID(undefined);
    intercomShutdown();
    intercomBoot();
  }, [intercomShutdown, intercomBoot]);

  // Multi-tab logout synchronization
  useEffect(() => {
    const cleanup = onLogoutBroadcast(() => {
      // Another tab logged out - clear local state and redirect
      resetAuthUI();
      window.location.pathname = '/login';
    });

    return cleanup;
  }, [resetAuthUI]);

  const trackLoggedInUserWithIntercom = (loggedInUser: IUser) => {
    const name = `${loggedInUser.first_name} ${loggedInUser.last_name}`.trim();
    const insertedAt = new Date(loggedInUser.inserted_at);

    intercomUpdate({
      email: loggedInUser.email,
      name,
      createdAt: Math.floor(insertedAt.getTime() / 1000),
      userHash: loggedInUser.user_hash,
    });
  };

  /**
   * Sets up authentication tokens in localStorage
   *
   * @param accessToken - The JWT access token for API authentication
   * @param refreshToken - Optional refresh token for obtaining new access tokens
   * @param rememberMe - Whether to persist the refresh token for longer sessions
   */
  const handleTokenSetup = (
    accessToken: string,
    refreshToken?: string,
    rememberMe?: boolean,
  ) => {
    localStorage.setItem('access_token', accessToken);
    if (rememberMe && refreshToken) {
      localStorage.setItem('refresh_token', refreshToken);
    }
  };

  /**
   * Sets up multi-factor authentication state in localStorage
   *
   * @param email - User's email address
   * @param mfaToken - Token required for MFA verification
   * @param rememberMe - Whether to persist session after MFA verification
   * @param activationKeyFromLogin - Optional activation key from login process
   */
  const handleMfaSetup = (
    email: string,
    mfaToken: string,
    rememberMe?: boolean,
    activationKeyFromLogin?: string,
  ) => {
    localStorage.setItem('mfa_email', email);
    localStorage.setItem('mfa_token', mfaToken);
    if (activationKeyFromLogin) {
      localStorage.setItem(
        'mfa_activation_key_from_login',
        activationKeyFromLogin,
      );
    }
    if (rememberMe) {
      localStorage.setItem('mfa_remember_me', rememberMe.toString());
    }
  };

  /**
   * Authentication behavior categories
   * - DIRECT: Authentication flows that complete immediately with tokens
   * - MFA: Authentication flows that require additional verification steps
   * - UNKNOWN: Fallback category for unrecognized authentication types
   */
  const AUTH_CATEGORIES = {
    DIRECT: 'direct_auth',
    MFA: 'mfa_flow',
    UNKNOWN: 'unknown',
  } as const;

  /** Type representing possible authentication behavior categories */
  type AuthCategory = (typeof AUTH_CATEGORIES)[keyof typeof AUTH_CATEGORIES];

  /** Authentication types that complete immediately with tokens */
  const DIRECT_AUTH_TYPES = [
    'password',
    'oauth_google',
    'oauth_microsoft',
    'sso',
  ];

  /** Authentication types that require additional verification steps */
  const MFA_FLOW_TYPES = ['mfa_email', 'mfa_app', 'mfa_sms'];

  /**
   * Maps authentication types to their behavior categories
   *
   * @param type - The authentication type string from the API
   * @returns The behavior category (DIRECT, MFA, or UNKNOWN)
   */
  const getAuthTypeCategory = (type: string): AuthCategory => {
    if (DIRECT_AUTH_TYPES.includes(type)) return AUTH_CATEGORIES.DIRECT;
    if (MFA_FLOW_TYPES.includes(type)) return AUTH_CATEGORIES.MFA;

    // Log unknown types for monitoring but handle gracefully
    const message = `Encountered new authentication_type: ${type}, treating as direct auth`;
    console.warn(message);

    // In a production environment, you might want to send this to your monitoring service
    // Example: logAuthEvent('unknown_auth_type', { type, message });

    return AUTH_CATEGORIES.DIRECT; // Default to direct auth for unknown types
  };

  /**
   * Performs the login process with the authentication API
   * Handles different authentication flows based on the response type
   *
   * @param credentials - Login credentials including email and password
   * @param successCallback - Function to call on successful authentication
   * @param isMfaShopify - Whether to use Shopify-specific MFA verification path
   */
  const performLogin = useCallback(
    (
      credentials: ILogin,
      successCallback: (
        activationKey?: string,
        activationKeyFromLogin?: string,
      ) => void,
      isMfaShopify: boolean = false,
    ) => {
      setLoading(true);
      accountsAPI
        .login({ email: credentials.email, password: credentials.password })
        .then(res => {
          setAuthError(false);
          const { authentication_type, access_token, refresh_token } =
            res.data.data;

          // Determine the behavior category for this authentication type
          const authCategory = getAuthTypeCategory(authentication_type);

          switch (authCategory) {
            case AUTH_CATEGORIES.DIRECT:
              // Handle successful token-based login
              handleTokenSetup(
                access_token,
                refresh_token,
                credentials.rememberMe,
              );
              getCurrentUser().then(() =>
                successCallback(
                  credentials.activationKey,
                  credentials.activationKeyFromLogin,
                ),
              );
              break;

            case AUTH_CATEGORIES.MFA:
              // Handle MFA redirect flow
              handleMfaSetup(
                credentials.email,
                access_token,
                credentials.rememberMe,
                credentials.activationKeyFromLogin,
              );
              navigate(isMfaShopify ? '/shopify/verify-mfa' : '/verify-mfa');
              break;

            case AUTH_CATEGORIES.UNKNOWN:
              // This case is unlikely to be reached due to our default handling in getAuthTypeCategory
              // but is included for completeness and future expansion
              console.error(
                `Unhandled authentication_type: ${authentication_type}`,
              );
              setAuthError(true);
              break;
          }
        })
        .catch(e => {
          // Extract useful error information for debugging and monitoring
          const errorDetails = {
            message: e.response?.data?.message || e.message,
            status: e.response?.status,
            email: credentials.email.replace(/(.{2})(.*)(@.*)/, '$1***$3'), // Partially mask email for privacy
          };

          console.error('Login failed:', errorDetails);

          // In a production environment, you might want to send this to your monitoring service
          // Example: logAuthEvent('login_error', errorDetails);

          setAuthError(true);
        })
        .finally(() => setLoading(false));
    },
    [getCurrentUser, navigate],
  );

  const login = useCallback(
    (credentials: ILogin) => {
      performLogin(
        credentials,
        (activationKey, activationKeyFromLogin) => {
          navigate('/', {
            state: {
              activationKey,
              activationKeyFromLogin,
            },
          });
        },
        false,
      );
    },
    [performLogin, navigate],
  );

  const verifyMfaCode = useCallback(
    (mfa_token: string, otp_code: string) => {
      setLoading(true);

      accountsAPI
        .verifyMfaToken(mfa_token, otp_code)
        .then(res => {
          const { access_token, refresh_token } = res.data.data;
          handleTokenSetup(
            access_token,
            refresh_token,
            !!localStorage.getItem('mfa_remember_me'),
          );
          localStorage.removeItem('mfa_token');
          localStorage.removeItem('mfa_email');
          localStorage.removeItem('mfa_remember_me');
          if (localStorage.getItem('mfa_activation_key_from_login')) {
            localStorage.removeItem('mfa_activation_key_from_login');
          }
        })
        .then(() => getCurrentUser())
        .catch(() => setAuthError(true))
        .finally(() => setLoading(false));
    },
    [getCurrentUser],
  );

  const signUp = useCallback(
    (
      email: string,
      firstName: string,
      lastName: string,
      organisationName: string,
      password: string,
      passwordConfirmation: string,
      termsAccepted: boolean,
      activationKey?: string,
    ) => {
      setLoading(true);

      accountsAPI
        .signup({
          email,
          first_name: firstName,
          last_name: lastName,
          organisation_name: organisationName,
          password,
          password_confirmation: passwordConfirmation,
          terms_accepted: termsAccepted,
        })
        .then(() => {
          login({ email, password, activationKey });
        })
        .catch(() => setAuthError(true))
        .finally(() => setLoading(false));
    },
    [login],
  );

  function logout() {
    // 1. Fire backend call (don't await - fire and forget)
    accountsAPI
      .logout()
      .catch(e => {
        // Log but don't block - token might already be invalid
        console.warn('Backend logout failed:', e.response?.status);
      })
      .finally(() => {
        // 2. Clear all local auth state immediately after backend call settles
        clearAuthState();

        // 3. Broadcast to other tabs
        broadcastLogout();

        // 4. Reset React state and Intercom
        resetAuthUI();
      });
  }

  function hasFeature(feature: string): boolean {
    return features.includes(feature);
  }

  const redirectToShopify = () => {
    try {
      const countryCode = detectUserCountry();
      const redirectUrl = getRedirectUrl(countryCode);
      console.info(
        `Shopify auth redirect: Using market-specific URL for ${countryCode} -> ${redirectUrl}`,
      );
      window.location.href = redirectUrl;
    } catch (error) {
      console.error('Failed to generate Shopify auth redirect URL:', error);
      // Fallback to default URL
      const fallbackUrl = 'https://tapt.io/collections/all-products';
      console.info(
        `Shopify auth redirect: Using fallback URL -> ${fallbackUrl}`,
      );
      window.location.href = fallbackUrl;
    }
  };

  const shopifyLogin = useCallback(
    (credentials: ILogin) => {
      performLogin(credentials, () => redirectToShopify(), true);
    },
    [performLogin, redirectToShopify],
  );

  const linkSocialAccount = useCallback(
    async (password: string, verifyToken: string) => {
      setLoading(true);
      setAuthError(false);
      try {
        const res = await accountsAPI.linkSocialAccount(password, verifyToken);
        const { access_token, refresh_token } = res.data.data;
        handleTokenSetup(access_token, refresh_token);
        getCurrentUser().then(() => {
          navigate('/');
        });
      } catch (e) {
        setAuthError(true);
      } finally {
        setLoading(false);
      }
    },
    [getCurrentUser, navigate],
  );

  const ssoLogin = useCallback(
    (access_token: string) => {
      handleTokenSetup(access_token);
      getCurrentUser().then(() => {
        navigate('/');
      });
    },
    [getCurrentUser, navigate],
  );

  const getPlatformSubscription = async (orgID?: number) => {
    if (!orgID) return;

    try {
      const res = await accountsAPI.getSubscription(orgID);
      setPlatformSubscription(res.data.subscription);
      setTotalProfiles(res.data.total_profiles);
    } catch (error) {
      setPlatformSubscription(null);
      setTotalProfiles(0);
    }
  };

  const memoedValue = useMemo(
    () => ({
      user,
      userScope,
      userRole,
      orgID,
      isUserViewForAdmin,
      loading,
      authError,
      platformSubscriptionStatus,
      platformSubscription,
      totalProfiles,
      features,
      adminRole,
      getCurrentUser,
      setScopeAndOrg,
      refreshCurrentUser,
      login,
      verifyMfaCode,
      signUp,
      logout,
      hasFeature,
      setAuthError,
      shopifyLogin,
      redirectToShopify,
      ssoLogin,
      linkSocialAccount,
    }),
    [
      getCurrentUser,
      setScopeAndOrg,
      refreshCurrentUser,
      login,
      logout,
      signUp,
      verifyMfaCode,
      hasFeature,
      setAuthError,
      shopifyLogin,
      redirectToShopify,
      ssoLogin,
      linkSocialAccount,
      user,
      userScope,
      userRole,
      orgID,
      isUserViewForAdmin,
      loading,
      authError,
      platformSubscriptionStatus,
      platformSubscription,
      totalProfiles,
      features,
      adminRole,
    ],
  );

  return (
    <AuthContext.Provider value={memoedValue}>
      {!loadingInitial && children}
    </AuthContext.Provider>
  );
}

export default function useAuth(): AuthContextType {
  return useContext(AuthContext);
}
